import { useMemo, useRef, useState } from "react";
import { useCenters } from "../../hooks/centers.hooks";
import { Center } from "../../types/center.types";
import { useClickAway } from "@uidotdev/usehooks";
import { Box, Flex, Text } from "@chakra-ui/layout";

const CenterSelect = ({
  placeholder = "Centre concerné",
  centerSelected,
  setCenterSelected,
}: {
  placeholder?: string;
  centerSelected?: Center | string;
  setCenterSelected: (center?: Center) => void;
}) => {
  const { centers } = useCenters();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState<string | null>(null);
  const searchBarRef = useRef<HTMLDivElement>(null);
  const folded = useMemo(
    () => centerSelected === undefined && open === false,
    [centerSelected, open]
  );
  const handleOpen = () => {
    if (searchBarRef.current && open === false) {
      setSearch(null);
      setOpen(true);
      searchBarRef.current.innerText = "";
      searchBarRef.current.focus();
    }
  };
  const handleClose = () => {
    if (searchBarRef.current && open === true) {
      setOpen(false);
      searchBarRef.current.innerText = "";
    }
  };
  const parentRef = useClickAway<HTMLDivElement>(handleClose);
  const handleUpdateFilter = () => {
    if (searchBarRef.current) {
      setSearch(
        searchBarRef.current.innerText.trim() === ""
          ? null
          : searchBarRef.current.innerText
      );
    }
  };
  const center = typeof centerSelected === "string" ? centers?.find((center) => center.id.toString() === centerSelected) : centerSelected;
  const CenterSelectLine = ({
    value,
    onClick,
    activated = false,
  }: {
    value: Center;
    onClick: () => void;
    activated?: boolean | null;
  }) => {
    return (
      <Flex
        justifyContent="space-between"
        alignItems="center"
        _hover={{
          bgColor: "brand.100",
          color: "brand.700",
        }}
        bgColor={activated ? "brand.100" : undefined}
        color={activated ? "brand.700" : undefined}
        px={4}
        py={2}
        gap={4}
        onClick={onClick}
      >
        <Text
          lineHeight="24px"
          fontSize="16px"
          fontWeight="500"
          color="gray.900"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {value.name}
        </Text>
      </Flex>
    );
  };
  return (
    <Box ref={parentRef} position="relative" onFocus={handleOpen} w="100%">
      {/* INPUT */}
      <Box
        w="100%"
        h="40px"
        px="1rem"
        lineHeight="40px"
        borderRadius="md"
        border="solid 1px"
        borderColor="gray.200"
        contentEditable
        ref={searchBarRef}
        onInput={handleUpdateFilter}
      ></Box>
      {open === false && (
        <Box position="absolute" top="0.6rem" left="1rem" onClick={handleOpen}>
          {center?.name}
        </Box>
      )}
      {/* PLACEHOLDER */}
      <Text
        position="absolute"
        marginBottom="-25px"
        top={folded ? "0.6rem" : "0px"}
        left="5px"
        transform={
          folded
            ? "translate(10px,0px) scale(1)"
            : "translate(10px,-10px) scale(0.8)"
        }
        color="gray.500"
        px={1}
        transformOrigin="top left"
        transition="all .2s ease-out"
        pointerEvents="none"
        bg="white"
        w="fit-content"
        h="fit-content"
        zIndex={5}
      >
        {placeholder}
      </Text>
      {/* DROPDOWN */}
      {open && (
        <Flex
          w="100%"
          height="min(fit-content,400px)"
          maxHeight="400px"
          position="absolute"
          top="44px"
          zIndex={1000}
          bgColor="white"
          direction={"column"}
          borderRadius="md"
          border="solid 1px"
          borderColor="gray.200"
          overflow="auto"
        >
          <Box
            flexGrow={1}
            overflow="hide"
            borderBottom="solid 1px"
            borderColor="gray.200"
          >
            {centers &&
              centers
                .filter((center) => {
                  if (search === null) {
                    return true;
                  }
                  const name = center.name.toLowerCase();
                  const searchFor = search.toLowerCase();
                  return name.includes(searchFor);
                })
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((c: Center) => (
                  <CenterSelectLine
                    key={c.id}
                    value={c}
                    onClick={() => {
                      setCenterSelected(c);
                      handleClose();
                    }}
                    activated={
                      c && c.id === center?.id
                    }
                  />
                ))}
          </Box>
        </Flex>
      )}
    </Box>
  );
};

export default CenterSelect;
